import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Button } from "flowbite-react";
import { AiOutlineLoading } from "react-icons/ai";
import { toast } from "react-toastify";
import { NavBar, SideNav } from "../../components";
import Breadcrumb from "../../components/Breadcrumb";
import { COUNTRY_NAMES } from "../../helpers/advisorVocab";
import { reviewInboxPath } from "../../helpers/reviewFilters";
import {
  clearReviewDetail,
  fetchChangeSet,
  publishChangeSet,
  rejectChangeSet,
} from "../../redux/slices/review";

const TIER_BADGE = {
  ready: "bg-emerald-50 text-emerald-800 border-emerald-200",
  check: "bg-amber-50 text-amber-900 border-amber-200",
  new: "bg-blue-50 text-blue-800 border-blue-200",
  needs_attention: "bg-red-50 text-red-800 border-red-200",
};

const TIER_LABEL = {
  ready: "Ready to publish",
  check: "Needs review",
  new: "New",
  needs_attention: "Needs attention",
};

const PREVIEW_FIELDS = [
  { key: "title", label: "Title", levels: ["programme", "school", "scholarship"] },
  { key: "country", label: "Country", levels: ["programme", "school", "scholarship"] },
  { key: "overview", label: "Overview", levels: ["programme", "school"] },
  { key: "degreeLevel", label: "Degree", levels: ["programme"] },
  { key: "fieldOfStudy", label: "Field of study", levels: ["programme"] },
  { key: "tuitionRaw", label: "Tuition", levels: ["programme"] },
  { key: "durationMonths", label: "Duration (months)", levels: ["programme"] },
  { key: "modeOfStudy", label: "Mode", levels: ["programme"] },
  { key: "languageOfInstruction", label: "Language", levels: ["programme"] },
  { key: "applyUrl", label: "Apply URL", levels: ["programme"] },
  {
    key: "department",
    label: "Department",
    nested: "programmeStructured",
    levels: ["programme"],
  },
  {
    key: "faculty",
    label: "Faculty",
    nested: "programmeStructured",
    levels: ["programme"],
  },
  {
    key: "ects",
    label: "ECTS",
    nested: "programmeStructured",
    levels: ["programme"],
  },
  {
    key: "curriculumText",
    label: "Curriculum",
    nested: "programmeStructured",
    levels: ["programme"],
  },
  {
    key: "location",
    label: "Location",
    nested: "schoolStructured",
    levels: ["school"],
  },
  {
    key: "universityType",
    label: "Type",
    nested: "schoolStructured",
    levels: ["school"],
  },
  {
    key: "website",
    label: "Website",
    nested: "schoolStructured",
    levels: ["school"],
  },
  {
    key: "extraFields",
    label: "Additional",
    nested: "schoolStructured",
    levels: ["school"],
  },
  { key: "coverage", label: "Coverage", levels: ["scholarship"] },
  { key: "amountRaw", label: "Amount", levels: ["scholarship"] },
  { key: "applicationUrl", label: "Application URL", levels: ["scholarship"] },
  { key: "eligibilityText", label: "Eligibility", levels: ["scholarship"] },
];

function humanizePath(path) {
  if (!path || path === "_entity") return null;
  return path
    .replace(/^structured\./, "")
    .replace(/^programmeStructured\./, "")
    .replace(/^schoolStructured\./, "")
    .replace(/^admission\./, "")
    .replace(/([A-Z])/g, " $1")
    .replace(/[_.]/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
}

function formatDisplayValue(value) {
  if (value == null || value === "") return null;
  if (Array.isArray(value)) {
    const joined = value
      .map((v) => (typeof v === "string" ? v.replace(/_/g, " ") : String(v)))
      .filter(Boolean)
      .join(", ");
    return joined || null;
  }
  if (typeof value === "object") {
    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return String(value);
    }
  }
  if (typeof value === "string" && /^[A-Z]{2}$/.test(value) && COUNTRY_NAMES[value]) {
    return COUNTRY_NAMES[value];
  }
  if (typeof value === "string" && value.includes("_") && value.length < 40) {
    return value.replace(/_/g, " ");
  }
  const text = String(value);
  if (text.length > 280) return `${text.slice(0, 277)}…`;
  return text;
}

function isPlainObject(value) {
  return value != null && typeof value === "object" && !Array.isArray(value);
}

function flattenPreviewRows(record, entityType) {
  if (!record || typeof record !== "object") return [];
  const rows = [];
  const seen = new Set();
  const fields = PREVIEW_FIELDS.filter(
    (f) => !entityType || !f.levels || f.levels.includes(entityType)
  );
  for (const field of fields) {
    const raw = field.nested
      ? record?.[field.nested]?.[field.key]
      : record?.[field.key];
    const display = formatDisplayValue(raw);
    if (!display) continue;
    const id = field.nested ? `${field.nested}.${field.key}` : field.key;
    seen.add(id);
    rows.push({ id, label: field.label, value: display });
  }
  // Include other top-level scalar-ish fields we didn't list
  for (const [key, raw] of Object.entries(record)) {
    if (
      [
        "body",
        "title",
        "category",
        "pageHashes",
        "evidence",
        "programmeStructured",
        "schoolStructured",
        "scholarshipStructured",
        "admission",
        "image",
        "createdAt",
        "updatedAt",
        "id",
        "articleId",
        "stableKey",
        "completenessScore",
        "version",
        "mergedInto",
      ].includes(key)
    ) {
      continue;
    }
    if (seen.has(key)) continue;
    if (isPlainObject(raw) || Array.isArray(raw)) continue;
    const display = formatDisplayValue(raw);
    if (!display) continue;
    rows.push({
      id: key,
      label: humanizePath(key) || key,
      value: display,
    });
  }
  return rows;
}

function liveDetailLink(item) {
  if (!item?.targetId) return null;
  const { entityType, targetId, schoolId } = item;
  if (entityType === "school") {
    return { to: `/school/${targetId}`, label: "Open live school" };
  }
  if (entityType === "scholarship") {
    return { to: `/scholarship/${targetId}`, label: "Open live scholarship" };
  }
  if (entityType === "programme" && schoolId) {
    return {
      to: `/school/${schoolId}/programmes?programme=${encodeURIComponent(
        targetId
      )}&isDraft=false`,
      label: "Open live programme",
    };
  }
  return null;
}

function changeTypeMeta(changeType) {
  if (changeType === "added") {
    return { label: "Added", className: "bg-emerald-50 text-emerald-800" };
  }
  if (changeType === "removed") {
    return { label: "Removed", className: "bg-red-50 text-red-800" };
  }
  return { label: "Updated", className: "bg-amber-50 text-amber-900" };
}

function FieldDiffCard({ row, isOn, editValue, onToggle, onEdit }) {
  const meta = changeTypeMeta(row.changeType);
  const label = row.label || humanizePath(row.path) || row.path;
  const before = formatDisplayValue(row.before);
  const afterRaw =
    editValue !== undefined ? editValue : formatDisplayValue(row.after);
  const isEntity = row.path === "_entity";
  const entityRows = isEntity && isPlainObject(row.after)
    ? flattenPreviewRows(row.after)
    : [];

  return (
    <div
      className={`rounded-xl border bg-white overflow-hidden ${
        isOn ? "border-purple-200 shadow-sm" : "border-gray-200 opacity-60"
      }`}
    >
      <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-b border-gray-100 bg-gray-50/80">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium text-gray-900">{label}</span>
            <span
              className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${meta.className}`}
            >
              {meta.label}
            </span>
            {row.priority === "critical" && (
              <span className="rounded-full bg-red-50 px-2 py-0.5 text-[11px] font-medium text-red-700">
                Critical
              </span>
            )}
            {typeof row.confidence === "number" && (
              <span className="text-[11px] text-gray-500">
                {Math.round(row.confidence * 100)}% confidence
              </span>
            )}
          </div>
          {!isEntity && (
            <div className="text-xs text-gray-400 mt-0.5 font-mono">{row.path}</div>
          )}
        </div>
        <label className="flex items-center gap-2 text-sm text-gray-700 shrink-0">
          <input
            type="checkbox"
            checked={isOn}
            onChange={(e) => onToggle(e.target.checked)}
            className="rounded border-gray-300 text-purple-700 focus:ring-purple-600"
          />
          Accept
        </label>
      </div>

      <div className="p-4">
        {isEntity ? (
          <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3">
            {entityRows.map((f) => (
              <div key={f.id} className="min-w-0">
                <div className="text-[11px] uppercase tracking-wide text-gray-400">
                  {f.label}
                </div>
                <div className="text-sm text-gray-900 mt-0.5 break-words">
                  {f.value}
                </div>
              </div>
            ))}
            {!entityRows.length && (
              <p className="text-sm text-gray-500">No structured fields yet.</p>
            )}
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-3">
            <div className="rounded-lg bg-gray-50 border border-gray-100 p-3">
              <div className="text-[11px] uppercase tracking-wide text-gray-400 mb-1">
                Current (live)
              </div>
              <div
                className={`text-sm whitespace-pre-wrap break-words ${
                  before ? "text-gray-500 line-through" : "text-gray-400"
                }`}
              >
                {before || "—"}
              </div>
            </div>
            <div className="rounded-lg bg-emerald-50/50 border border-emerald-100 p-3">
              <div className="text-[11px] uppercase tracking-wide text-emerald-700/70 mb-1">
                Proposed
              </div>
              {row.changeType === "removed" ? (
                <div className="text-sm text-red-700">Will be removed</div>
              ) : (
                <textarea
                  className="w-full rounded-md border-emerald-200 bg-white/80 p-2 text-sm text-gray-900 focus:border-purple-400 focus:ring-purple-400"
                  rows={Math.min(
                    6,
                    Math.max(2, String(afterRaw || "").split("\n").length)
                  )}
                  value={afterRaw || ""}
                  onChange={(e) => onEdit(e.target.value)}
                />
              )}
            </div>
          </div>
        )}

        {row.evidence?.quote && (
          <blockquote className="mt-3 border-l-4 border-teal-400 pl-3 text-sm text-gray-600 italic">
            “{row.evidence.quote}”
            {row.evidence.url && (
              <a
                href={`${row.evidence.url}${
                  row.evidence.quote
                    ? `#:~:text=${encodeURIComponent(
                        row.evidence.quote.slice(0, 80)
                      )}`
                    : ""
                }`}
                target="_blank"
                rel="noreferrer"
                className="ml-2 not-italic text-teal-700 hover:underline"
              >
                Source
              </a>
            )}
          </blockquote>
        )}
      </div>
    </div>
  );
}

function SnapshotCard({ title, subtitle, rows, emptyText }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-100 bg-gray-50/80">
        <h3 className="font-medium text-gray-900">{title}</h3>
        {subtitle && (
          <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>
        )}
      </div>
      <div className="p-4">
        {rows.length ? (
          <dl className="space-y-3">
            {rows.map((row) => (
              <div key={row.id}>
                <dt className="text-[11px] uppercase tracking-wide text-gray-400">
                  {row.label}
                </dt>
                <dd className="text-sm text-gray-900 mt-0.5 break-words">
                  {row.value}
                </dd>
              </div>
            ))}
          </dl>
        ) : (
          <p className="text-sm text-gray-500">{emptyText}</p>
        )}
      </div>
    </div>
  );
}

function getFieldRaw(record, field) {
  if (!record) return undefined;
  return field.nested
    ? record?.[field.nested]?.[field.key]
    : record?.[field.key];
}

function buildExtractedCompareRows(
  baseline,
  proposed,
  diff = [],
  opts = {}
) {
  const { showEmpty = true, entityType } = opts;
  const diffByPath = new Map(
    (diff || [])
      .filter((d) => d?.path && d.path !== "_entity")
      .map((d) => [d.path, d])
  );
  const rows = [];
  const seen = new Set();
  const fields = PREVIEW_FIELDS.filter(
    (f) => !entityType || !f.levels || f.levels.includes(entityType)
  );

  for (const field of fields) {
    const path = field.nested ? `${field.nested}.${field.key}` : field.key;
    seen.add(path);
    const beforeRaw = getFieldRaw(baseline, field);
    const afterRaw = getFieldRaw(proposed, field);
    const before = formatDisplayValue(beforeRaw);
    const after = formatDisplayValue(afterRaw);
    const d = diffByPath.get(path);
    if (!showEmpty && !before && !after && !d) continue;
    let change = "same";
    if (d) {
      change =
        d.changeType === "changed" ? "updated" : d.changeType || "updated";
    } else if (before !== after) {
      if (!before && after) change = "added";
      else if (before && !after) change = "removed";
      else change = "updated";
    }
    rows.push({
      path,
      label: field.label,
      before,
      after,
      change,
    });
  }

  for (const d of diff || []) {
    if (!d?.path || d.path === "_entity" || seen.has(d.path)) continue;
    rows.push({
      path: d.path,
      label: d.label || humanizePath(d.path) || d.path,
      before: formatDisplayValue(d.before),
      after: formatDisplayValue(d.after),
      change:
        d.changeType === "changed" ? "updated" : d.changeType || "updated",
    });
  }

  // Prefer changed rows first so the full table still surfaces what moved
  return rows.sort((a, b) => {
    const rank = (c) =>
      c === "added" || c === "removed" || c === "updated" || c === "changed"
        ? 0
        : 1;
    return rank(a.change) - rank(b.change);
  });
}

function ExtractedFieldsTable({ rows }) {
  if (!rows?.length) {
    return (
      <p className="text-sm text-gray-500 rounded-xl border border-dashed border-gray-200 bg-white p-6 text-center">
        No extracted fields to show yet.
      </p>
    );
  }
  const changedCount = rows.filter((r) => r.change !== "same").length;
  return (
    <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-100 bg-gray-50/80">
        <div className="font-medium text-gray-900">Extracted fields</div>
        <p className="text-xs text-gray-500 mt-0.5">
          All fields the crawler tries to extract — current vs proposed
          {changedCount ? ` · ${changedCount} changed` : ""}
        </p>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-left text-gray-600">
            <tr>
              <th className="px-4 py-2 font-medium">Field</th>
              <th className="px-4 py-2 font-medium">Current</th>
              <th className="px-4 py-2 font-medium">Proposed</th>
              <th className="px-4 py-2 font-medium">Change</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const meta = changeTypeMeta(row.change);
              const changed = row.change !== "same";
              return (
                <tr
                  key={row.path}
                  className={`border-t border-gray-100 ${
                    changed ? "bg-amber-50/40" : ""
                  }`}
                >
                  <td className="px-4 py-2.5 font-medium text-gray-800 whitespace-nowrap">
                    {row.label}
                  </td>
                  <td className="px-4 py-2.5 text-gray-600 max-w-xs">
                    {row.before || (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-gray-900 max-w-xs">
                    {row.change === "removed" ? (
                      <span className="text-red-700">Removed</span>
                    ) : (
                      row.after || <span className="text-gray-400">—</span>
                    )}
                  </td>
                  <td className="px-4 py-2.5">
                    {changed ? (
                      <span
                        className={`rounded-full px-2 py-0.5 text-xs ${meta.className}`}
                      >
                        {meta.label}
                      </span>
                    ) : (
                      <span className="text-xs text-gray-400">Same</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const ChangeReview = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    current,
    proposed,
    live,
    baseline,
    baselineId,
    baselineIsDraft,
    schoolGate,
    detailLoading,
    actionLoading,
    error,
  } = useSelector((state) => state.review);

  const [accepted, setAccepted] = useState({});
  const [edits, setEdits] = useState({});
  // llm = template + AI cleanup, template = structured HTML only, keep = leave body
  const [bodyMode, setBodyMode] = useState("template");

  useEffect(() => {
    dispatch(fetchChangeSet(id));
    return () => dispatch(clearReviewDetail());
  }, [dispatch, id]);

  useEffect(() => {
    if (!current?.diff) return;
    const next = {};
    current.diff.forEach((d) => {
      next[d.path] = d.changeType !== "removed";
    });
    setAccepted(next);
  }, [current]);

  const acceptedFields = useMemo(
    () => Object.keys(accepted).filter((k) => accepted[k]),
    [accepted]
  );

  const liveLink = useMemo(() => liveDetailLink(current), [current]);

  const baselineRecord = baseline || live || null;

  const proposedRecord = useMemo(() => {
    if (proposed && Object.keys(proposed).length) return proposed;
    const entity = (current?.diff || []).find((d) => d.path === "_entity");
    if (entity?.after && typeof entity.after === "object") return entity.after;
    return baselineRecord;
  }, [proposed, current, baselineRecord]);

  const extractedRows = useMemo(
    () =>
      buildExtractedCompareRows(
        baselineRecord,
        proposedRecord,
        current?.diff || [],
        { entityType: current?.entityType || "programme", showEmpty: true }
      ),
    [baselineRecord, proposedRecord, current?.diff, current?.entityType]
  );

  const schoolLink = current?.schoolId
    ? {
        to: `/school/${current.schoolId}${
          current.targetId || !baselineIsDraft
            ? "?isDraft=false"
            : "?isDraft=true"
        }`,
        label:
          current.schoolName ||
          schoolGate?.schoolTitle ||
          "View school",
      }
    : null;

  const programmeLink =
    baselineId && current?.schoolId && current?.entityType === "programme"
      ? {
          to: `/school/${current.schoolId}/programmes?programme=${encodeURIComponent(
            baselineId
          )}&isDraft=${baselineIsDraft ? "true" : "false"}`,
          label: baselineIsDraft ? "View draft programme" : "View programme",
        }
      : null;

  const schoolProgrammesLink =
    current?.schoolId && current?.entityType === "programme"
      ? {
          to: `/school/${current.schoolId}/programmes?isDraft=${
            baselineIsDraft || !current.targetId ? "true" : "false"
          }`,
          label: "School programmes",
        }
      : null;

  const schoolBlocked =
    (current?.entityType === "programme" ||
      current?.entityType === "scholarship") &&
    !current?.targetId &&
    schoolGate &&
    !schoolGate.schoolIsLive;

  const onPublish = async () => {
    if (!current) return;
    if (schoolBlocked) {
      toast.error(
        "Publish the school first — programmes cannot go live while the school is still a draft."
      );
      return;
    }
    const isFirstPublish = !current.targetId || current.tier === "new";
    let fields = [...acceptedFields];
    if (!fields.length) {
      if (isFirstPublish) {
        // Publish full snapshot; unchecked diffs are reverted to current values.
        fields = ["_entity"];
      } else {
        toast.error("Accept at least one field");
        return;
      }
    }
    const result = await dispatch(
      publishChangeSet({
        id: current.id,
        acceptedFields: fields,
        edits,
        revision: current.revision,
        convertBody: bodyMode !== "keep",
        polishBody: bodyMode === "llm",
      })
    );
    if (publishChangeSet.fulfilled.match(result)) {
      toast.success("Published");
      navigate(reviewInboxPath());
    } else {
      toast.error(result.payload || "Publish failed");
    }
  };

  const onReject = async () => {
    if (!current) return;
    const result = await dispatch(
      rejectChangeSet({
        id: current.id,
        revision: current.revision,
        reason: "Rejected in review UI",
      })
    );
    if (rejectChangeSet.fulfilled.match(result)) {
      toast.success("Rejected");
      navigate(reviewInboxPath());
    } else {
      toast.error(result.payload || "Reject failed");
    }
  };

  const acceptAll = () => {
    if (!current?.diff) return;
    const next = {};
    current.diff.forEach((d) => {
      next[d.path] = true;
    });
    setAccepted(next);
  };

  return (
    <div className="antialiased bg-gray-50 dark:bg-gray-900 min-h-screen pb-28">
      <NavBar />
      <SideNav />
      <main className="p-4 md:ml-64 h-auto pt-20">
        <div className="sticky top-14 z-10 bg-gray-50 dark:bg-gray-900">
          <Breadcrumb />
        </div>

        <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <Link
              to={reviewInboxPath()}
              className="text-sm text-purple-800 hover:underline"
            >
              ← Back to inbox
            </Link>
            <h2 className="text-xl font-semibold text-gray-900 mt-1 truncate">
              {current?.title || "Change review"}
            </h2>
            <div className="mt-2 flex flex-wrap items-center gap-2 text-sm">
              <span className="capitalize text-gray-500">
                {current?.entityType || "—"}
              </span>
              {current?.tier && (
                <span
                  className={`rounded-full border px-2 py-0.5 text-xs font-medium ${
                    TIER_BADGE[current.tier] ||
                    "bg-gray-100 text-gray-700 border-gray-200"
                  }`}
                >
                  {TIER_LABEL[current.tier] || current.tier}
                </span>
              )}
              {liveLink && (
                <Link
                  to={liveLink.to}
                  className="text-teal-700 hover:underline"
                >
                  {liveLink.label}
                </Link>
              )}
              {programmeLink && (
                <Link
                  to={programmeLink.to}
                  className="text-teal-700 hover:underline"
                >
                  {programmeLink.label}
                </Link>
              )}
              {schoolLink && current?.entityType !== "school" && (
                <Link
                  to={schoolLink.to}
                  className="text-teal-700 hover:underline"
                >
                  {schoolLink.label === "View school"
                    ? schoolLink.label
                    : `School · ${schoolLink.label}`}
                </Link>
              )}
              {schoolProgrammesLink && (
                <Link
                  to={schoolProgrammesLink.to}
                  className="text-teal-700 hover:underline"
                >
                  {schoolProgrammesLink.label}
                </Link>
              )}
              {current?.sourceUrl && (
                <a
                  href={current.sourceUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-teal-700 hover:underline"
                >
                  Source site
                </a>
              )}
            </div>
          </div>
          {detailLoading && (
            <AiOutlineLoading className="animate-spin text-purple-700" />
          )}
        </div>

        {error && (
          <p className="text-sm text-red-600 mb-3">{String(error)}</p>
        )}

        {schoolBlocked && (
          <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950">
            <div className="font-medium">
              School is still a draft — publish it before this{" "}
              {current?.entityType}.
            </div>
            <p className="mt-1 text-amber-900/80">
              {schoolGate.schoolTitle
                ? `“${schoolGate.schoolTitle}” `
                : "The linked school "}
              exists only as a draft. Live programmes need a published school
              parent (same rule as publishing from the school page).
            </p>
            <div className="mt-2 flex flex-wrap gap-3">
              {schoolGate.schoolChangeSetId ? (
                <Link
                  to={`/review/${schoolGate.schoolChangeSetId}`}
                  className="font-medium text-teal-800 hover:underline"
                >
                  Review school change set →
                </Link>
              ) : (
                schoolGate.schoolIsDraft &&
                schoolGate.schoolId && (
                  <Link
                    to={`/school/${schoolGate.schoolId}?isDraft=true`}
                    className="font-medium text-teal-800 hover:underline"
                  >
                    Open draft school →
                  </Link>
                )
              )}
            </div>
          </div>
        )}

        <div className="grid lg:grid-cols-5 gap-4">
          <div className="lg:col-span-3 space-y-3">
            <ExtractedFieldsTable rows={extractedRows} />

            <div className="flex flex-wrap items-center gap-3 mb-1 pt-2">
              <h3 className="text-sm font-medium text-gray-800 mr-2">
                Accept changes
              </h3>
              <Button size="xs" color="light" onClick={acceptAll}>
                Accept all
              </Button>
              <label className="flex items-center gap-2 text-sm text-gray-600">
                Body
                <select
                  className="rounded-lg border-gray-300 text-sm"
                  value={bodyMode}
                  onChange={(e) => setBodyMode(e.target.value)}
                >
                  <option value="template">Template only</option>
                  <option value="llm">LLM cleanup</option>
                  <option value="keep">Keep existing body</option>
                </select>
              </label>
              <span className="text-xs text-gray-400">
                {(current?.diff || []).length} change
                {(current?.diff || []).length === 1 ? "" : "s"}
              </span>
            </div>
            <p className="text-xs text-gray-500 -mt-1 mb-2">
              Only checked fields are applied. Unchecked changes keep the
              current value.
            </p>

            {(current?.diff || []).map((row) => (
              <FieldDiffCard
                key={row.path}
                row={row}
                isOn={!!accepted[row.path]}
                editValue={edits[row.path]}
                onToggle={(checked) =>
                  setAccepted((prev) => ({ ...prev, [row.path]: checked }))
                }
                onEdit={(value) =>
                  setEdits((prev) => ({ ...prev, [row.path]: value }))
                }
              />
            ))}

            {!detailLoading && !(current?.diff || []).length && (
              <p className="text-gray-500 text-sm rounded-xl border border-dashed border-gray-200 bg-white p-6 text-center">
                No field diffs on this set.
              </p>
            )}
          </div>

          <aside className="lg:col-span-2 space-y-3 lg:sticky lg:top-24 self-start">
            <div id="change-preview">
              <SnapshotCard
                title="Proposed fields"
                subtitle={
                  current?.targetId
                    ? "What will be published"
                    : "Reconstructed from crawl + current draft/live"
                }
                rows={flattenPreviewRows(
                  proposedRecord,
                  current?.entityType
                )}
                emptyText="No proposed fields available."
              />
            </div>
            <SnapshotCard
              title={baselineIsDraft ? "Current draft" : "Live today"}
              subtitle={
                baselineRecord
                  ? baselineIsDraft
                    ? "Existing draft this crawl matched"
                    : "Currently published version"
                  : "Nothing on file for this item"
              }
              rows={flattenPreviewRows(
                baselineRecord,
                current?.entityType
              )}
              emptyText="Not published yet."
            />
            <div className="rounded-xl border border-gray-200 bg-white p-4 text-sm text-gray-600">
              <div className="font-medium text-gray-900 mb-2">Run stats</div>
              <div>
                Coverage{" "}
                <span className="font-medium text-gray-900">
                  {Math.round((current?.coverageBefore || 0) * 100)}% →{" "}
                  {Math.round((current?.coverageAfter || 0) * 100)}%
                </span>
              </div>
              <div className="mt-1 text-gray-500">
                Revision {current?.revision ?? "—"} · base v
                {current?.baseVersion ?? 0}
              </div>
              {(current?.reasons || []).length > 0 && (
                <ul className="mt-3 space-y-1">
                  {current.reasons.map((r) => (
                    <li
                      key={r}
                      className="rounded-md bg-gray-50 px-2 py-1 text-xs text-gray-600"
                    >
                      {r.replace(/_/g, " ")}
                    </li>
                  ))}
                </ul>
              )}
              {programmeLink && (
                <Link
                  to={programmeLink.to}
                  className="mt-3 inline-block text-teal-700 hover:underline"
                >
                  {programmeLink.label} →
                </Link>
              )}
            </div>
          </aside>
        </div>
      </main>

      <div className="fixed bottom-0 left-0 right-0 md:left-64 border-t border-gray-200 bg-white/95 backdrop-blur px-4 py-3 flex justify-end gap-2 z-20">
        <Button color="light" onClick={onReject} disabled={actionLoading}>
          Reject
        </Button>
        <Button
          color="purple"
          onClick={onPublish}
          disabled={actionLoading || schoolBlocked}
        >
          {actionLoading ? (
            <AiOutlineLoading className="animate-spin" />
          ) : schoolBlocked ? (
            "Publish school first"
          ) : (
            `Publish ${acceptedFields.length} changes`
          )}
        </Button>
      </div>
    </div>
  );
};

export default ChangeReview;
